// ── MOBILE ──────────────────────────────────────────────────────────────────
// Phase 1: Bottom nav, Today view, Quick capture, Search, Card detail sheet
// Phase 2: Swipe nav, Card edit, Cap polish, Voice input, @delegate capture

import { e, parseContext, formatShortDate, normalizeStatus, getDueDateUrgency } from './modules/utils.js';
import { backgroundFetch } from './modules/network.js';
import { PRESET_COLORS, STATUS_COLORS } from './modules/state.js';
import { SESSION_USER_KEY, logout, getAuthHeader } from './modules/auth-client.js';
import { loadOverrides, saveOverrides, cloudSave } from './modules/storage.js';
import { writeDelegateHistory, refreshDelegateQueue, showDelegateToast, openDelegatePrompt, showDiscardUndoToast, showSendConfirmModal } from './modules/delegate-client.js';
import { parseNaturalInput, getColumnNames } from './modules/capture.js';
import { boardData, AI_NAME } from './core.js';
import { mergeOverrides } from './data.js';
import { renderFromState } from './render.js';

// ── Module-private state ──
let _mobileInited = false;
let _mobileActiveTab = 'today';
let _mobileCaptureParseResult = null;
let _mobileCaptureParseId = null;
let _mobileCaptureOriginalText = null;
let _mobileCapturePollTimer = null;
let _mobileCaptureFireTimer = null;
let _mobileCaptureParseTimer = null;
let _mobileCaptureRipe = false;
let _mobileCapParseAttempts = 0;
let _mobileSearchTimer = null;
let _mobilePlaceholderTimer = null;
let _mobileCurrentCard = null;
let _mobileCurrentCardColName = null;
let _mobileBoardColIdx = 0;
let _mobileBoardColumns = [];
let _mobileBoardTouchStartX = 0;
let _mobileBoardTouchDeltaX = 0;
let _mobileCapBadgePollTimer = null;
let _mobileRevisionArtifactKey = null;
let _mobileSpeechRecognition = null;
let _mobileBatchReviewQueue = [];
let _mobileBatchReviewIdx = 0;

export function isMobileViewport() { return window.innerWidth <= 520; }

export function initMobile() {
  if (_mobileInited) return;
  _mobileInited = true;

  // ── Tab switching ──
  document.querySelectorAll('.mobile-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      const t = tab.dataset.tab;
      switchMobileTab(t);
    });
  });

  // ── Mobile account chip + sign-out sheet ──
  const mobileAccountChip = document.getElementById('mobile-account-chip');
  if (mobileAccountChip) {
    const username = localStorage.getItem(SESSION_USER_KEY) || '';
    mobileAccountChip.textContent = (username.charAt(0) || '?').toUpperCase();
    mobileAccountChip.addEventListener('click', () => {
      const uname = localStorage.getItem(SESSION_USER_KEY) || '';
      document.getElementById('mobile-account-user').textContent = 'Signed in as ' + (uname || 'unknown');
      document.getElementById('mobile-account-backdrop').classList.add('visible');
    });
    document.getElementById('mobile-account-signout').addEventListener('click', () => {
      document.getElementById('mobile-account-backdrop').classList.remove('visible');
      logout();
    });
    document.getElementById('mobile-account-cancel').addEventListener('click', () => {
      document.getElementById('mobile-account-backdrop').classList.remove('visible');
    });
    document.getElementById('mobile-account-backdrop').addEventListener('click', (ev) => {
      if (ev.target === document.getElementById('mobile-account-backdrop')) {
        document.getElementById('mobile-account-backdrop').classList.remove('visible');
      }
    });
  }

  // ── First-use mobile nav hint ──
  const MOBILE_HINT_KEY = 'refocus_mobile_hint_v1';
  if (!localStorage.getItem(MOBILE_HINT_KEY)) {
    const todayTab = document.querySelector('.mobile-tab[data-tab=today]');
    if (todayTab) {
      const hint = document.createElement('div');
      hint.className = 'mobile-tab-hint';
      hint.textContent = 'Today \u00b7 Board \u00b7 Capture \u00b7 Search \u00b7 AI';
      document.querySelector('.mobile-bottom-nav')?.appendChild(hint);
      setTimeout(() => {
        hint.classList.add('fade-out');
        setTimeout(() => hint.remove(), 500);
        localStorage.setItem(MOBILE_HINT_KEY, '1');
      }, 3000);
    }
  }

  // ── Capture view events ──
  const captureInput = document.getElementById('mobile-capture-input');
  const captureSubmit = document.getElementById('mobile-capture-submit');
  const captureCancel = document.getElementById('mobile-capture-cancel');
  const captureRevertBtn = document.getElementById('mobile-capture-revert-btn');

  captureCancel.addEventListener('click', () => switchMobileTab('today'));
  captureSubmit.addEventListener('click', submitMobileCapture);
  captureInput.addEventListener('input', onMobileCaptureInput);

  // Mobile capture ripe toggle
  const mobileRipeCapture = document.getElementById('mobile-capture-ripe-toggle');
  if (mobileRipeCapture) {
    mobileRipeCapture.addEventListener('click', () => {
      _mobileCaptureRipe = !_mobileCaptureRipe;
      mobileRipeCapture.style.opacity = _mobileCaptureRipe ? '1' : '0.3';
    });
  }
  captureInput.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); submitMobileCapture(); }
  });
  captureRevertBtn.addEventListener('click', () => {
    if (_mobileCaptureOriginalText) {
      captureInput.value = _mobileCaptureOriginalText;
      _mobileCaptureOriginalText = null;
      document.getElementById('mobile-capture-revert').classList.add('hidden');
      document.getElementById('mobile-capture-preview').classList.add('hidden');
    }
  });

  // ── Search scroll to dismiss keyboard ──
  document.getElementById('mobile-search-results').addEventListener('scroll', function() {
    document.getElementById('mobile-search-input').blur();
  }, { passive: true });

  // ── Card detail events ──
  const cardBackdrop = document.getElementById('mobile-card-backdrop');
  const cardClose = document.getElementById('mobile-card-close');
  cardBackdrop.addEventListener('click', (ev) => { if (ev.target === cardBackdrop) closeMobileCardDetail(); });
  cardClose.addEventListener('click', closeMobileCardDetail);
  setupSheetSwipeDismiss(document.getElementById('mobile-card-detail'), closeMobileCardDetail);

  document.getElementById('mobile-card-delegate-btn').addEventListener('click', () => {
    if (_mobileCurrentCard) {
      closeMobileCardDetail();
      // Open existing desktop delegate modal flow — works at any viewport
      const proj = _mobileCurrentCard;
      if (typeof openDelegatePrompt === 'function') openDelegatePrompt(proj);
    }
  });

  // ── Search events ──
  const searchInput = document.getElementById('mobile-search-input');
  searchInput.addEventListener('input', () => {
    clearTimeout(_mobileSearchTimer);
    _mobileSearchTimer = setTimeout(() => runMobileSearch(searchInput.value), 300);
  });
  searchInput.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') { searchInput.value = ''; runMobileSearch(''); }
  });

  // ── Phase 2: Board view events ──
  document.getElementById('mobile-board-prev').addEventListener('click', () => {
    navigateMobileBoard(_mobileBoardColIdx - 1);
  });
  document.getElementById('mobile-board-next').addEventListener('click', () => {
    navigateMobileBoard(_mobileBoardColIdx + 1);
  });
  // Swipe gestures on board content
  const boardContent = document.getElementById('mobile-board-content');
  boardContent.addEventListener('touchstart', (ev) => {
    _mobileBoardTouchStartX = ev.touches[0].clientX;
    _mobileBoardTouchDeltaX = 0;
    const slider = boardContent.querySelector('.mobile-board-slider');
    if (slider) slider.classList.add('no-transition');
  }, { passive: true });
  boardContent.addEventListener('touchmove', (ev) => {
    _mobileBoardTouchDeltaX = ev.touches[0].clientX - _mobileBoardTouchStartX;
    const slider = boardContent.querySelector('.mobile-board-slider');
    if (slider) {
      const base = -_mobileBoardColIdx * 100;
      const pct = (_mobileBoardTouchDeltaX / boardContent.offsetWidth) * 100;
      slider.style.transform = `translateX(${base + pct}%)`;
    }
  }, { passive: true });
  boardContent.addEventListener('touchend', () => {
    const slider = boardContent.querySelector('.mobile-board-slider');
    if (slider) slider.classList.remove('no-transition');
    if (Math.abs(_mobileBoardTouchDeltaX) > 50) {
      if (_mobileBoardTouchDeltaX > 0 && _mobileBoardColIdx > 0) {
        navigateMobileBoard(_mobileBoardColIdx - 1);
      } else if (_mobileBoardTouchDeltaX < 0 && _mobileBoardColIdx < _mobileBoardColumns.length - 1) {
        navigateMobileBoard(_mobileBoardColIdx + 1);
      } else {
        navigateMobileBoard(_mobileBoardColIdx); // snap back
      }
    } else {
      navigateMobileBoard(_mobileBoardColIdx); // snap back
    }
    _mobileBoardTouchDeltaX = 0;
  }, { passive: true });

  // ── Phase 2: Artifact review sheet ──
  const artifactBackdrop = document.getElementById('mobile-artifact-backdrop');
  if (artifactBackdrop) {
    const artifactClose = document.getElementById('mobile-artifact-close');
    artifactBackdrop.addEventListener('click', (ev) => { if (ev.target === artifactBackdrop) closeMobileArtifactReview(); });
    artifactClose.addEventListener('click', closeMobileArtifactReview);
    setupSheetSwipeDismiss(document.getElementById('mobile-artifact-sheet'), closeMobileArtifactReview);
  }

  // ── Phase 2: Voice input ──
  initMobileVoiceInput();

  // ── Phase 2: Revision sheet events ──
  const revBackdrop = document.getElementById('mobile-revision-backdrop');
  revBackdrop.addEventListener('click', (ev) => { if (ev.target === revBackdrop) closeMobileRevisionSheet(); });
  document.getElementById('mobile-revision-cancel').addEventListener('click', closeMobileRevisionSheet);
  document.getElementById('mobile-revision-send').addEventListener('click', submitMobileRevision);
  setupSheetSwipeDismiss(document.getElementById('mobile-revision-sheet'), closeMobileRevisionSheet);

  // ── Phase 2: Cap badge polling ──
  startMobileCapBadgePoll();

  // Initial render if already mobile
  if (isMobileViewport() && boardData) {
    renderMobileTodayView();
    renderMobileDate();
  }
}

// ── Called by init.js after each renderFromState ──
export function onMobileRenderComplete() {
  if (isMobileViewport() && boardData) {
    renderMobileTodayView();
    updateMobileCapBadge();
    if (_mobileActiveTab === 'board') renderMobileBoardView();
  }
}

// ── Tab switching ──
function switchMobileTab(tab) {
  _mobileActiveTab = tab;
  document.querySelectorAll('.mobile-tab').forEach(t => {
    t.classList.toggle('mobile-tab-active', t.dataset.tab === tab);
  });
  document.querySelectorAll('.mobile-view').forEach(v => v.classList.remove('mobile-view-active'));
  const viewMap = { today: 'mobile-today', search: 'mobile-search-view', cap: 'mobile-cap-view', board: 'mobile-board-view', capture: 'mobile-capture-view' };
  const viewEl = document.getElementById(viewMap[tab]);
  if (viewEl) viewEl.classList.add('mobile-view-active');

  if (tab === 'today' && boardData) { renderMobileTodayView(); renderMobileDate(); }
  if (tab === 'search') {
    setTimeout(() => document.getElementById('mobile-search-input').focus(), 100);
  }
  if (tab === 'cap') renderMobileCapView();
  if (tab === 'board') renderMobileBoardView();
  if (tab === 'capture') openMobileCaptureView();
}

// ── Date helper ──
function renderMobileDate() {
  const el = document.getElementById('mobile-date');
  if (!el) return;
  const d = new Date();
  const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  el.textContent = `${days[d.getDay()]} ${months[d.getMonth()]} ${d.getDate()}`;
}

// ── Today View ──
function renderMobileTodayView() {
  const container = document.getElementById('mobile-today-content');
  if (!container || !boardData) return;

  const data = mergeOverrides(boardData);
  const ov = loadOverrides();
  const todayStr = new Date().toISOString().split('T')[0];

  // Collect all ripe tasks across all cards
  const ripeTasks = [];
  function walkCards(cards, colName) {
    for (const card of cards) {
      if (card.archived) continue;
      if (ov.archivedCards?.[card.id]) continue;
      for (const t of (card.tasks || [])) {
        if (t.ripe && !t.done) {
          // Skip if snoozed today
          if (t.ripeSnoozedDate === todayStr) continue;
          ripeTasks.push({ task: t, card, colName });
        }
      }
      if (card.children?.length) walkCards(card.children, colName);
    }
  }
  for (const col of data.columns) {
    const colName = (ov.columnRenames && ov.columnRenames[col.id]) || col.name;
    if (ov.archivedColumns && ov.archivedColumns.includes(col.id)) continue;
    walkCards(col.projects || [], colName);
  }

  // Load intentions from localStorage
  const intentionsRaw = localStorage.getItem('todayIntentions');
  const todayIntentions = intentionsRaw ? JSON.parse(intentionsRaw) : [];

  // Build intentions data
  const intentionCards = [];
  if (todayIntentions.length > 0) {
    for (const cardId of todayIntentions) {
      let found = null;
      let foundCol = '';
      function findCard(cards, cn) {
        for (const c of cards) {
          if (c.id === cardId) { found = c; foundCol = cn; return; }
          if (c.children?.length) findCard(c.children, cn);
        }
      }
      for (const col of data.columns) {
        const cn = (ov.columnRenames && ov.columnRenames[col.id]) || col.name;
        findCard(col.projects || [], cn);
      }
      if (found) {
        const futureNowTasks = (found.tasks || []).filter(t => !t.done && !t.ripe);
        intentionCards.push({ card: found, colName: foundCol, futureNowTasks });
      }
    }
  }

  // Cap updates
  const capArtifacts = Object.entries(ov.delegateArtifacts || {});

  if (!ripeTasks.length && !intentionCards.length && !capArtifacts.length) {
    container.innerHTML = '<div class="mobile-today-empty"><div class="mobile-today-empty-icon">\u2713</div>All clear \u2014 nothing ripe today</div>';
    return;
  }

  // Ripe count warning (>5 = heavy)
  const ripeCountClass = ripeTasks.length > 5 ? ' ripe-heavy' : '';

  let html = '';

  // Section 1: READY (ripe tasks)
  html += `<div class="mobile-section mobile-section-ripe${ripeCountClass}">`;
  html += `<div class="mobile-section-header">\ud83c\udf51 READY <span class="section-count" style="background:rgba(255,180,120,0.25);color:#ffb478">${ripeTasks.length}</span></div>`;
  if (ripeTasks.length === 0) {
    html += '<div class="mobile-today-empty" style="padding:12px 0;font-size:0.82rem">No ripe tasks \u2014 mark tasks with \ud83c\udf51 at capture</div>';
  } else {
    for (const { task, card, colName } of ripeTasks) {
      const { displayText } = parseContext(task.text);
      html += `<div class="mobile-today-task-item" data-task-id="${e(task.id)}" data-card-id="${e(card.id)}">
        <div class="mobile-today-task-main">
          <span class="mobile-today-task-text">\ud83c\udf51 ${e(displayText)}</span>
          <span class="mobile-today-task-context">${e(card.name)} \u203a ${e(colName)}</span>
        </div>
      </div>`;
    }
  }
  html += '</div>';

  // Section 2: INTENTIONS
  html += '<div class="mobile-section mobile-section-intentions">';
  html += `<div class="mobile-section-header">\ud83c\udfaf INTENTIONS <span class="section-count" style="background:rgba(122,162,247,0.2);color:#7aa2f7">${intentionCards.length}</span></div>`;
  if (intentionCards.length === 0) {
    html += '<div class="mobile-today-intentions-empty" data-action="set-intentions">Tap to set today\u2019s intentions</div>';
  } else {
    for (const { card, colName, futureNowTasks } of intentionCards) {
      html += `<div class="mobile-today-intention" data-card-id="${e(card.id)}" data-col-name="${e(colName)}">
        <div class="mobile-today-intention-header">
          <span class="mobile-today-intention-name">${e(card.name)}</span>
          <span class="mobile-today-intention-count">${futureNowTasks.length} item${futureNowTasks.length !== 1 ? 's' : ''}</span>
        </div>
        ${futureNowTasks.slice(0, 5).map(t => {
          const { displayText } = parseContext(t.text);
          return `<div class="mobile-today-intention-task">\u203a ${e(displayText)}</div>`;
        }).join('')}
      </div>`;
    }
    html += '<div class="mobile-today-intentions-edit" data-action="set-intentions">Edit intentions</div>';
  }
  html += '</div>';

  // Cap updates
  if (capArtifacts.length) {
    html += `<div class="mobile-section mobile-section-cap">
      <div class="mobile-section-header"><span class="cap-glyph">\u25c8</span> ${e(AI_NAME)} has updates <span class="section-count" style="background:#bb9af7;color:#1a1b26">${capArtifacts.length}</span></div>
      <div class="mobile-card-item" data-action="open-cap">
        <span class="mobile-card-item-name" style="color:#bb9af7">Tap to review ${capArtifacts.length} artifact${capArtifacts.length > 1 ? 's' : ''}</span>
      </div>
    </div>`;
  }

  container.innerHTML = html;

  // Wire swipe handlers on ripe tasks
  container.querySelectorAll('.mobile-today-task-item').forEach(el => {
    let touchStartX = 0;
    let touchDeltaX = 0;
    el.addEventListener('touchstart', (ev) => {
      touchStartX = ev.touches[0].clientX;
      touchDeltaX = 0;
    }, { passive: true });
    el.addEventListener('touchmove', (ev) => {
      touchDeltaX = ev.touches[0].clientX - touchStartX;
      if (Math.abs(touchDeltaX) > 10) {
        el.style.transform = `translateX(${touchDeltaX}px)`;
        el.style.opacity = String(Math.max(0.3, 1 - Math.abs(touchDeltaX) / 200));
      }
    }, { passive: true });
    el.addEventListener('touchend', () => {
      const taskId = el.dataset.taskId;
      if (touchDeltaX > 80) {
        // Swipe right = complete task
        el.style.transform = 'translateX(100%)';
        el.style.opacity = '0';
        setTimeout(() => {
          const ov2 = loadOverrides();
          if (!ov2.taskStates) ov2.taskStates = {};
          ov2.taskStates[taskId] = true;
          if (!ov2.taskDoneOverrides) ov2.taskDoneOverrides = {};
          ov2.taskDoneOverrides[taskId] = { done: true, doneAt: new Date().toISOString() };
          ov2.taskCompletedAt = ov2.taskCompletedAt || {};
          ov2.taskCompletedAt[taskId] = new Date().toISOString();
          saveOverrides(ov2);
          renderFromState();
        }, 200);
      } else if (touchDeltaX < -80) {
        // Swipe left = not today (snooze ripe until tomorrow)
        el.style.transform = 'translateX(-100%)';
        el.style.opacity = '0';
        setTimeout(() => {
          const ov2 = loadOverrides();
          if (!ov2.taskRipeSnoozed) ov2.taskRipeSnoozed = {};
          ov2.taskRipeSnoozed[taskId] = todayStr;
          saveOverrides(ov2);
          renderFromState();
        }, 200);
      } else {
        el.style.transform = '';
        el.style.opacity = '';
      }
    });
  });

  // Wire intention card taps (open card detail)
  container.querySelectorAll('.mobile-today-intention[data-card-id]').forEach(el => {
    el.addEventListener('click', () => {
      const cardId = el.dataset.cardId;
      const colName = el.dataset.colName;
      const d2 = mergeOverrides(boardData);
      let found = null;
      function find(cards) { for (const c of cards) { if (c.id === cardId) { found = c; return; } if (c.children?.length) find(c.children); } }
      for (const col of d2.columns) find(col.projects || []);
      if (found) openMobileCardDetail(found, colName);
    });
  });

  // Wire intentions set/edit
  container.querySelectorAll('[data-action="set-intentions"]').forEach(el => {
    el.addEventListener('click', () => openIntentionsPicker());
  });

  // Wire cap tap
  container.querySelectorAll('.mobile-card-item[data-action="open-cap"]').forEach(el => {
    el.addEventListener('click', () => switchMobileTab('cap'));
  });
}

// ── Intentions Picker Modal ──
function openIntentionsPicker() {
  const data = mergeOverrides(boardData);
  const ov = loadOverrides();
  const existing = JSON.parse(localStorage.getItem('todayIntentions') || '[]');
  const selected = new Set(existing);

  // Collect all non-archived, non-done cards
  const allCards = [];
  for (const col of data.columns) {
    if (ov.archivedColumns && ov.archivedColumns.includes(col.id)) continue;
    const colName = (ov.columnRenames && ov.columnRenames[col.id]) || col.name;
    for (const card of (col.projects || [])) {
      if (card.archived || ov.archivedCards?.[card.id]) continue;
      const cStatus = normalizeStatus(card.status || 'Active');
      if (cStatus === 'Done') continue;
      allCards.push({ card, colName });
    }
  }

  // Create modal
  const backdrop = document.createElement('div');
  backdrop.className = 'mobile-sheet-backdrop open';
  backdrop.id = 'intentions-picker-backdrop';
  const sheet = document.createElement('div');
  sheet.className = 'mobile-sheet';
  sheet.innerHTML = `
    <div class="mobile-sheet-handle"></div>
    <h3 style="margin:0 0 12px;font-size:1rem;color:var(--text)">\ud83c\udfaf Set Today\u2019s Intentions</h3>
    <p style="margin:0 0 12px;font-size:0.78rem;color:var(--text-dim)">Pick 1\u20133 cards to focus on today</p>
    <div class="intentions-card-list">
      ${allCards.map(({ card, colName }) => `
        <label class="intentions-card-option${selected.has(card.id) ? ' selected' : ''}" data-card-id="${e(card.id)}">
          <input type="checkbox" ${selected.has(card.id) ? 'checked' : ''}>
          <span class="intentions-card-name">${e(card.name)}</span>
          <span class="intentions-card-col">${e(colName)}</span>
        </label>
      `).join('')}
    </div>
    <div style="display:flex;gap:8px;margin-top:16px">
      <button class="mobile-capture-cancel-btn" id="intentions-cancel">Cancel</button>
      <button class="mobile-capture-submit-btn" id="intentions-save">Save</button>
    </div>
  `;
  backdrop.appendChild(sheet);
  document.body.appendChild(backdrop);

  // Wire handlers
  sheet.querySelectorAll('.intentions-card-option input').forEach(cb => {
    cb.addEventListener('change', () => {
      const cardId = cb.closest('.intentions-card-option').dataset.cardId;
      if (cb.checked) selected.add(cardId);
      else selected.delete(cardId);
      cb.closest('.intentions-card-option').classList.toggle('selected', cb.checked);
      // Max 3
      if (selected.size >= 3) {
        sheet.querySelectorAll('.intentions-card-option input:not(:checked)').forEach(c => c.disabled = true);
      } else {
        sheet.querySelectorAll('.intentions-card-option input').forEach(c => c.disabled = false);
      }
    });
  });
  document.getElementById('intentions-cancel').addEventListener('click', () => backdrop.remove());
  document.getElementById('intentions-save').addEventListener('click', () => {
    localStorage.setItem('todayIntentions', JSON.stringify([...selected]));
    backdrop.remove();
    renderMobileTodayView();
  });
  backdrop.addEventListener('click', (ev) => { if (ev.target === backdrop) backdrop.remove(); });
}

// ── Capture View (full-screen) ──
function openMobileCaptureView() {
  const input = document.getElementById('mobile-capture-input');
  input.value = '';
  _mobileCaptureParseResult = null;
  _mobileCaptureOriginalText = null;
  _mobileCaptureRipe = false;
  const mobileRipeTgl = document.getElementById('mobile-capture-ripe-toggle');
  if (mobileRipeTgl) mobileRipeTgl.style.opacity = '0.3';
  document.getElementById('mobile-capture-preview').classList.add('hidden');
  document.getElementById('mobile-capture-preview').innerHTML = '';
  document.getElementById('mobile-capture-revert').classList.add('hidden');
  document.getElementById('mobile-capture-hint').classList.remove('hidden');
  clearInterval(_mobileCapturePollTimer);
  clearTimeout(_mobileCaptureFireTimer);
  clearTimeout(_mobileCaptureParseTimer);

  setTimeout(() => input.focus(), 300);
  startMobilePlaceholderCycle();
}

const MOBILE_PLACEHOLDERS = [
  'What needs doing?',
  'Draft follow-up for Josh…',
  'Check in with Tyler re: April tour…',
  '@delegate research venue options…',
  'Email caterer about dietary needs @email',
  'Call venue re: deposit @call',
];
let _mobilePlaceholderIdx = 0;

function startMobilePlaceholderCycle() {
  _mobilePlaceholderIdx = 0;
  const input = document.getElementById('mobile-capture-input');
  _mobilePlaceholderTimer = setInterval(() => {
    if (input.value) return; // don't cycle if user is typing
    _mobilePlaceholderIdx = (_mobilePlaceholderIdx + 1) % MOBILE_PLACEHOLDERS.length;
    input.placeholder = MOBILE_PLACEHOLDERS[_mobilePlaceholderIdx];
  }, 3000);
}

function onMobileCaptureInput() {
  clearTimeout(_mobileCaptureParseTimer);
  clearTimeout(_mobileCaptureFireTimer);
  updateMobileCaptureDelegate();
  _mobileCaptureParseTimer = setTimeout(() => {
    const text = document.getElementById('mobile-capture-input').value.trim();
    if (!text) {
      _mobileCaptureParseResult = null;
      document.getElementById('mobile-capture-preview').classList.add('hidden');
      document.getElementById('mobile-capture-hint').classList.remove('hidden');
      return;
    }
    const parsed = parseNaturalInput(text);
    _mobileCaptureParseResult = parsed;
    renderMobileCapturePreview(parsed);

    // Fire Cap parse for low-confidence entries
    if (parsed.confidence === 'low' && text.length > 10) {
      _mobileCaptureFireTimer = setTimeout(() => fireMobileCapParse(text), 1000);
    }
  }, 400);
}

function renderMobileCapturePreview(parsed) {
  const preview = document.getElementById('mobile-capture-preview');
  const hint = document.getElementById('mobile-capture-hint');
  if (!parsed) { preview.classList.add('hidden'); hint.classList.remove('hidden'); return; }
  hint.classList.add('hidden');
  preview.classList.remove('hidden');
  let html = '';
  if (parsed.taskText) html += `<div class="parse-preview-row"><span class="parse-label">Task</span><span class="parse-value">${e(parsed.taskText)}</span></div>`;
  if (parsed.cardName) html += `<div class="parse-preview-row"><span class="parse-label">Card</span><span class="parse-value">${e(parsed.cardName)}</span></div>`;
  if (parsed.dueDate) html += `<div class="parse-preview-row"><span class="parse-label">Due</span><span class="parse-value">${e(parsed.dueDate)}</span></div>`;
  if (parsed.context) html += `<div class="parse-preview-row"><span class="parse-label">Context</span><span class="parse-value">${e(parsed.context)}</span></div>`;
  if (parsed.column) {
    const col = getColumnNames().find(c => c.id === parsed.column);
    if (col) html += `<div class="parse-preview-row"><span class="parse-label">Column</span><span class="parse-value">${e(col.name)}</span></div>`;
  }
  preview.innerHTML = html;
}

function fireMobileCapParse(text) {
  if (_mobileCaptureParseId) { clearInterval(_mobileCapturePollTimer); _mobileCaptureParseId = null; }
  _mobileCapParseAttempts = 0;
  _mobileCaptureOriginalText = null;
  fetch('/api/v1/parse-capture', {
    method: 'POST',
    headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ rawText: text }),
  })
    .then(r => r.json())
    .then(data => {
      if (data.parseId) {
        _mobileCaptureParseId = data.parseId;
        _mobileCapturePollTimer = setInterval(() => pollMobileCapParse(text), 4000);
      }
    })
    .catch((err) => { console.warn('[Re:Focus] Mobile capture parse failed:', err.message || err); });
}

function pollMobileCapParse(originalText) {
  if (!_mobileCaptureParseId) return;
  if (++_mobileCapParseAttempts > 7) {
    clearInterval(_mobileCapturePollTimer);
    _mobileCaptureParseId = null;
    return;
  }
  fetch(`/api/v1/parse-capture?parseId=${encodeURIComponent(_mobileCaptureParseId)}`, {
    headers: getAuthHeader(),
  })
    .then(r => r.json())
    .then(data => {
      if (data.status === 'ready' && data.result) {
        clearInterval(_mobileCapturePollTimer);
        _mobileCaptureParseId = null;
        const currentText = document.getElementById('mobile-capture-input')?.value?.trim() || '';
        if (currentText !== originalText) return; // stale
        _mobileCaptureParseResult = data.result;
        renderMobileCapturePreview(data.result);

        // Silent cleanup — detect dictation artifacts
        if (data.result.taskText || data.result.cardName) {
          const cleaned = cleanDictationText(currentText);
          if (cleaned !== currentText) {
            _mobileCaptureOriginalText = currentText;
            document.getElementById('mobile-capture-input').value = cleaned;
            document.getElementById('mobile-capture-revert').classList.remove('hidden');
          }
        }
      } else if (data.status === 'failed') {
        clearInterval(_mobileCapturePollTimer);
        _mobileCaptureParseId = null;
      }
    })
    .catch((err) => { console.warn('[Re:Focus] Mobile parse poll failed:', err.message || err); });
}

function cleanDictationText(text) {
  let t = text.trim();
  // All lowercase + no punctuation at end → likely dictation
  if (t === t.toLowerCase() && t.length > 5 && !/[.!?]$/.test(t)) {
    // Capitalize first letter
    t = t.charAt(0).toUpperCase() + t.slice(1);
  }
  return t;
}

function submitMobileCapture() {
  const text = document.getElementById('mobile-capture-input').value.trim();
  if (!text) return;

  // Phase 2: standalone @delegate path — create a card then enqueue delegation
  if (/^@delegate\b/i.test(text)) {
    const instruction = text.replace(/^@delegate\s*/i, '').trim();
    if (!instruction) return;
    const projId = 'capture-' + Date.now();
    const ov = loadOverrides();
    if (!ov.addedProjects) ov.addedProjects = [];
    const cols = getColumnNames();
    const colId = cols[0] ? cols[0].id : 'default';
    const newCard = {
      id: projId, name: instruction,
      color: PRESET_COLORS[ov.addedProjects.length % PRESET_COLORS.length],
      createdAt: new Date().toISOString().split('T')[0],
      columnId: colId,
      tasks: [], status: '', outcome: '', contacts: [], comms: []
    };
    ov.addedProjects.push(newCard);
    saveOverrides(ov);
    fetch('/api/v1/delegate', {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cardId: projId,
        cardName: instruction,
        taskId: null,
        taskText: instruction.slice(0, 500),
        cardScope: '',
        cardNotes: '',
      }),
    }).catch(err => console.warn('[delegate] standalone enqueue failed:', err.message));
    switchMobileTab('today');
    showDelegateToast('Delegated to ' + AI_NAME + ' \u2197');
    renderFromState();
    return;
  }

  const parsed = _mobileCaptureParseResult || parseNaturalInput(text);

  // Determine column
  const cols = getColumnNames();
  const colId = parsed.column || (cols[0] ? cols[0].id : 'default');

  const projId = 'capture-' + Date.now();
  const taskId = projId + '-t1';
  const ov = loadOverrides();
  if (!ov.addedProjects) ov.addedProjects = [];

  const dueDate = parsed.dueDate || null;
  const taskText = parsed.taskText || (parsed.cardName ? null : text);
  const cardName = parsed.cardName || text;

  const newCard = {
    id: projId, name: cardName,
    color: PRESET_COLORS[ov.addedProjects.length % PRESET_COLORS.length],
    createdAt: new Date().toISOString().split('T')[0],
    columnId: colId,
    tasks: taskText ? [{ id: taskId, text: taskText, done: false, dueDate }] : [],
    status: '', outcome: '', contacts: [], comms: []
  };
  ov.addedProjects.push(newCard);
  // Save ripe flag on tasks if toggled
  if (_mobileCaptureRipe && newCard.tasks.length > 0) {
    if (!ov.taskRipe) ov.taskRipe = {};
    for (const t of newCard.tasks) ov.taskRipe[t.id] = true;
    _mobileCaptureRipe = false;
  }
  saveOverrides(ov);

  // Enqueue @delegate (if text contains @delegate anywhere but not at the start — that's handled above)
  if (/@delegate\b/i.test(taskText)) {
    fetch('/api/v1/delegate', {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cardId: projId, cardName, taskId, taskText,
        cardScope: '', cardNotes: '',
      }),
    }).catch((err) => { console.warn('[Re:Focus] Mobile delegate enqueue failed:', err.message || err); });
  }

  switchMobileTab('today');
  renderFromState();
}

// ── Search ──
function runMobileSearch(q) {
  const results = document.getElementById('mobile-search-results');
  if (!q.trim()) { results.innerHTML = ''; return; }
  if (!boardData) { results.innerHTML = '<div class="mobile-search-empty">Loading\u2026</div>'; return; }

  const data = mergeOverrides(boardData);
  const hits = [];
  const lower = q.toLowerCase();
  const ov = loadOverrides();

  function searchCards(cards, colName, path) {
    for (const card of cards) {
      if (card.archived) continue;
      const nameMatch = card.name.toLowerCase().includes(lower);
      const taskMatch = (card.tasks || []).some(t => t.text.toLowerCase().includes(lower));
      const scopeMatch = (card.outcome || '').toLowerCase().includes(lower);
      const commsMatch = (card.comms || []).some(c => c.text?.toLowerCase().includes(lower));
      const progressMatch = (ov.cardProgressNotes?.[card.id] || '').toLowerCase().includes(lower);
      if (nameMatch || taskMatch || scopeMatch || commsMatch || progressMatch) {
        hits.push({ card, colName, path: [...path], snippet: getSearchSnippet(card, lower) });
      }
      if (card.children?.length) searchCards(card.children, colName, [...path, card.name]);
    }
  }

  for (const col of data.columns) {
    if (ov.archivedColumns && ov.archivedColumns.includes(col.id)) continue;
    const colName = (ov.columnRenames && ov.columnRenames[col.id]) || col.name;
    searchCards(col.projects || [], colName, [colName]);
  }

  if (!hits.length) {
    results.innerHTML = '<div class="mobile-search-empty">No results</div>';
    return;
  }

  results.innerHTML = hits.slice(0, 20).map((h, i) => {
    const status = normalizeStatus(h.card.status || 'Active');
    const sc = STATUS_COLORS[status] || STATUS_COLORS['Active'];
    return `<div class="mobile-card-item" data-search-idx="${i}">
      <span class="mobile-card-item-name">${e(h.card.name)}</span>
      <div class="mobile-card-item-meta">
        <span>${e(h.colName)}</span>
        <span class="mobile-card-status-chip" style="background:${sc.bg};color:${sc.color}">${e(status)}</span>
        ${h.snippet ? `<span>${e(h.snippet)}</span>` : ''}
      </div>
    </div>`;
  }).join('');

  results.querySelectorAll('.mobile-card-item').forEach((el, i) => {
    el.addEventListener('click', () => {
      const h = hits[i];
      if (h) openMobileCardDetail(h.card, h.colName);
    });
  });
}

function getSearchSnippet(card, lower) {
  for (const t of (card.tasks || [])) {
    if (t.text.toLowerCase().includes(lower)) {
      const txt = t.text.length > 50 ? t.text.slice(0, 50) + '\u2026' : t.text;
      return txt;
    }
  }
  if ((card.outcome || '').toLowerCase().includes(lower)) return 'in scope';
  for (const c of (card.comms || [])) {
    if ((c.text || '').toLowerCase().includes(lower)) return 'in comms';
  }
  return '';
}

// ── Card Detail Bottom Sheet (Phase 2: full edit) ──
function openMobileCardDetail(card, colName) {
  _mobileCurrentCard = card;
  _mobileCurrentCardColName = colName;
  const backdrop = document.getElementById('mobile-card-backdrop');

  // Editable card name
  const nameEl = document.getElementById('mobile-card-name');
  nameEl.textContent = card.name;
  nameEl.contentEditable = 'true';
  nameEl.classList.add('mobile-card-name-editable');
  nameEl.addEventListener('blur', handleMobileCardNameEdit, { once: true });
  nameEl.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') { ev.preventDefault(); nameEl.blur(); }
  });

  // Tappable status chip — cycles through statuses
  const status = normalizeStatus(card.status || 'Active');
  const statusChip = document.getElementById('mobile-card-status');
  const sc = STATUS_COLORS[status] || STATUS_COLORS['Active'];
  statusChip.textContent = status;
  statusChip.style.background = sc.bg;
  statusChip.style.color = sc.color;
  statusChip.classList.add('mobile-status-chip-tap');
  statusChip.onclick = () => cycleMobileCardStatus();

  document.getElementById('mobile-card-context').textContent = colName || '';
  document.getElementById('mobile-delegate-ai-name').textContent = AI_NAME;

  // Body content
  const body = document.getElementById('mobile-card-detail-body');
  let html = '';

  // Scope — editable textarea with delegate hint
  html += `<div class="mobile-detail-section">
    <div class="mobile-detail-section-label">Scope / Notes</div>
    <textarea class="mobile-detail-scope-editable" id="mobile-scope-edit" data-card-id="${e(card.id)}">${e(card.outcome || '')}</textarea>
    <div class="mobile-scope-hint">or start with @delegate to hand off to ${e(AI_NAME)}</div>
  </div>`;

  // Tasks — with checkboxes, nest hints, + add task input
  const tasks = card.tasks || [];
  html += `<div class="mobile-detail-section">
    <div class="mobile-detail-section-label">Tasks</div>
    <ul class="mobile-task-list">`;
  for (const t of tasks) {
    const doneClass = t.done ? ' done' : '';
    const nestHint = t.text.length > 80 ? '<span class="mobile-task-nest-hint">\u21b3 Will become a sub-card</span>' : '';
    html += `<li class="mobile-task-item${doneClass}">
      <input type="checkbox" ${t.done ? 'checked' : ''} data-task-id="${e(t.id)}" data-card-id="${e(card.id)}">
      ${t.ripe ? '<span class="task-ripe-indicator">\ud83d\udd37</span>' : ''}
      <span>${e(t.text)}</span>
      ${nestHint}
    </li>`;
  }
  html += `</ul>
    <div class="mobile-add-task-row">
      <input type="text" class="mobile-add-task-input" placeholder="Next physical step\u2026" maxlength="200">
      <button class="mobile-ripe-task-toggle ripe-toggle" title="Mark as Cue" style="opacity:0.3">\ud83d\udd37</button>
      <button class="mobile-add-task-btn">+</button>
    </div>
  </div>`;

  // Contacts — with add contact
  const contacts = card.contacts || [];
  html += `<div class="mobile-detail-section">
    <div class="mobile-detail-section-label">Contacts</div>`;
  for (const c of contacts) {
    html += `<div class="mobile-contact-item">${e(c.name)}${c.role ? ` <span class="mobile-contact-role">${e(c.role)}</span>` : ''}${c.email ? ` <span class="mobile-contact-role">${e(c.email)}</span>` : ''}</div>`;
  }
  html += `<div class="mobile-add-contact-row">
      <button class="mobile-add-contact-btn">+ Add contact</button>
      <div class="mobile-add-contact-form hidden">
        <input type="text" class="mobile-add-task-input mobile-contact-name" placeholder="Name" maxlength="100" autocomplete="off">
        <input type="text" class="mobile-add-task-input mobile-contact-role-input" placeholder="Role (optional)" maxlength="100" autocomplete="off">
        <button class="mobile-contact-save-btn">Save</button>
      </div>
    </div>
  </div>`;

  // Due date
  if (card.dueDate) {
    html += `<div class="mobile-detail-section">
      <div class="mobile-detail-section-label">Due Date</div>
      <div style="font-size:0.88rem;color:var(--text)">${e(formatShortDate(card.dueDate))}</div>
    </div>`;
  }

  body.innerHTML = html;

  // Wire task checkboxes
  body.querySelectorAll('input[type="checkbox"][data-task-id]').forEach(cb => {
    cb.addEventListener('change', () => {
      const taskId = cb.dataset.taskId;
      const cardId = cb.dataset.cardId;
      const ov = loadOverrides();
      if (!ov.taskStates) ov.taskStates = {};
      ov.taskStates[taskId] = cb.checked;
      if (cb.checked) {
        ov.taskCompletedAt = ov.taskCompletedAt || {};
        ov.taskCompletedAt[taskId] = new Date().toISOString();
        if (!ov.taskDoneOverrides) ov.taskDoneOverrides = {};
        ov.taskDoneOverrides[taskId] = { done: true, doneAt: new Date().toISOString() };
      } else {
        if (ov.taskDoneOverrides) delete ov.taskDoneOverrides[taskId];
        if (ov.taskCompletedAt) delete ov.taskCompletedAt[taskId];
      }
      saveOverrides(ov);
      // Optimistic UI update
      cb.closest('.mobile-task-item').classList.toggle('done', cb.checked);
      renderFromState();
    });
  });

  // Wire scope edit
  const scopeEdit = body.querySelector('#mobile-scope-edit');
  if (scopeEdit) {
    let scopeDebounce = null;
    scopeEdit.addEventListener('input', () => {
      clearTimeout(scopeDebounce);
      scopeDebounce = setTimeout(() => {
        const cardId = scopeEdit.dataset.cardId;
        const ov = loadOverrides();
        if (!ov.cardOutcomes) ov.cardOutcomes = {};
        ov.cardOutcomes[cardId] = scopeEdit.value;
        saveOverrides(ov);
        showMobileSavedToast();
        renderFromState();
      }, 600);
    });
  }

  // Wire add task
  const addTaskInput = body.querySelector('.mobile-add-task-input:not(.mobile-contact-name):not(.mobile-contact-role-input)');
  const addTaskBtn = body.querySelector('.mobile-add-task-btn');
  const mobileRipeToggle = body.querySelector('.mobile-ripe-task-toggle');
  let _mobileAddTaskRipe = false;
  if (mobileRipeToggle) {
    mobileRipeToggle.addEventListener('click', () => {
      _mobileAddTaskRipe = !_mobileAddTaskRipe;
      mobileRipeToggle.style.opacity = _mobileAddTaskRipe ? '1' : '0.3';
    });
  }
  const addTask = () => {
    const text = addTaskInput.value.trim();
    if (!text) return;
    const hasCueTrigger = _mobileAddTaskRipe || /@(?:quick|cue)\b/i.test(text) || /\bquick\b/i.test(text);
    const cleanText = text.replace(/@(?:quick|cue)\b/gi, '').replace(/\bquick\b/gi, '').replace(/\s{2,}/g, ' ').trim();
    const taskId = card.id + '-t' + Date.now();
    const ov = loadOverrides();
    if (!ov.addedTasks) ov.addedTasks = {};
    if (!ov.addedTasks[card.id]) ov.addedTasks[card.id] = [];
    ov.addedTasks[card.id].push({ id: taskId, text: cleanText, done: false });
    if (hasCueTrigger) {
      if (!ov.taskRipe) ov.taskRipe = {};
      ov.taskRipe[taskId] = true;
      _mobileAddTaskRipe = false;
    }
    saveOverrides(ov);
    // Enqueue @delegate tasks to AI worker (mirrors desktop addTask path)
    if (/@delegate/i.test(text)) {
      fetch('/api/v1/delegate', {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ cardId: card.id, cardName: card.name || '', taskId, taskText: text, cardScope: card.outcome || '', cardNotes: '' }),
      }).catch((err) => { console.warn('[Re:Focus] Mobile delegate enqueue failed:', err.message || err); });
    }
    addTaskInput.value = '';
    showMobileSavedToast();
    // Re-render card detail
    const data = mergeOverrides(boardData);
    let updatedCard = null;
    function findCard(cards) { for (const c of cards) { if (c.id === card.id) { updatedCard = c; return; } if (c.children?.length) findCard(c.children); } }
    for (const col of data.columns) findCard(col.projects || []);
    if (updatedCard) {
      _mobileCurrentCard = updatedCard;
      openMobileCardDetail(updatedCard, colName);
    }
    renderFromState();
  };
  addTaskBtn.addEventListener('click', addTask);
  addTaskInput.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') { ev.preventDefault(); addTask(); }
  });

  // Wire add contact
  const addContactBtn = body.querySelector('.mobile-add-contact-btn');
  const addContactForm = body.querySelector('.mobile-add-contact-form');
  if (addContactBtn && addContactForm) {
    addContactBtn.addEventListener('click', () => {
      addContactForm.classList.remove('hidden');
      addContactBtn.style.display = 'none';
      addContactForm.querySelector('.mobile-contact-name').focus();
    });
    const saveContactBtn = addContactForm.querySelector('.mobile-contact-save-btn');
    saveContactBtn.addEventListener('click', () => {
      const nameInput = addContactForm.querySelector('.mobile-contact-name');
      const roleInput = addContactForm.querySelector('.mobile-contact-role-input');
      const name = nameInput.value.trim();
      if (!name) return;
      const role = roleInput.value.trim();
      const ov = loadOverrides();
      if (!ov.cardContacts) ov.cardContacts = {};
      if (!ov.cardContacts[card.id]) ov.cardContacts[card.id] = [...(card.contacts || [])];
      ov.cardContacts[card.id].push({ name, role });
      saveOverrides(ov);
      nameInput.value = '';
      roleInput.value = '';
      addContactForm.classList.add('hidden');
      addContactBtn.style.display = '';
      showMobileSavedToast();
      // Re-render card detail
      const data = mergeOverrides(boardData);
      let updatedCard = null;
      function findCard(cards) { for (const c of cards) { if (c.id === card.id) { updatedCard = c; return; } if (c.children?.length) findCard(c.children); } }
      for (const col of data.columns) findCard(col.projects || []);
      if (updatedCard) {
        _mobileCurrentCard = updatedCard;
        openMobileCardDetail(updatedCard, colName);
      }
      renderFromState();
    });
  }

  backdrop.classList.add('open');
}

function handleMobileCardNameEdit() {
  const nameEl = document.getElementById('mobile-card-name');
  const newName = nameEl.textContent.trim();
  if (!_mobileCurrentCard || !newName || newName === _mobileCurrentCard.name) return;
  const ov = loadOverrides();
  if (!ov.cardNames) ov.cardNames = {};
  ov.cardNames[_mobileCurrentCard.id] = newName;
  saveOverrides(ov);
  showMobileSavedToast();
  renderFromState();
}

function cycleMobileCardStatus() {
  if (!_mobileCurrentCard) return;
  // Build cycle: Active, Waiting, Done
  const statusCycle = ['Active', 'Waiting', 'Done'];
  const current = normalizeStatus(_mobileCurrentCard.status || 'Active');
  const idx = statusCycle.indexOf(current);
  const next = statusCycle[(idx + 1) % statusCycle.length];
  const ov = loadOverrides();
  if (!ov.cardStatusOverrides) ov.cardStatusOverrides = {};
  ov.cardStatusOverrides[_mobileCurrentCard.id] = { status: next, doneAt: next === 'Done' ? new Date().toISOString() : null };
  if (!ov.cardStatus) ov.cardStatus = {};
  ov.cardStatus[_mobileCurrentCard.id] = next;
  saveOverrides(ov);
  // Update chip immediately
  const statusChip = document.getElementById('mobile-card-status');
  const sc = STATUS_COLORS[next] || STATUS_COLORS['Active'];
  statusChip.textContent = next;
  statusChip.style.background = sc.bg;
  statusChip.style.color = sc.color;
  showMobileSavedToast();
  renderFromState();
}

function closeMobileCardDetail() {
  document.getElementById('mobile-card-backdrop').classList.remove('open');
  const nameEl = document.getElementById('mobile-card-name');
  nameEl.contentEditable = 'false';
  nameEl.classList.remove('mobile-card-name-editable');
  _mobileCurrentCard = null;
  _mobileCurrentCardColName = null;
}

// ── Cap Activity View (Phase 2: approve, reject, pulse, empty state, batch review) ──
async function renderMobileCapView() {
  const content = document.getElementById('mobile-cap-content');
  document.getElementById('mobile-cap-title').textContent = AI_NAME;
  content.innerHTML = '<div class="mobile-loading">Loading\u2026</div>';

  try {
    const resp = await fetch('/api/v1/delegate', { headers: getAuthHeader() });
    if (!resp.ok) { content.innerHTML = `<div class="mobile-today-empty">Could not load ${e(AI_NAME)} data.</div>`; return; }
    const data = await resp.json();
    const queue = data.queue || data.items || data || [];
    const items = Array.isArray(queue) ? queue : [];
    const inProgress = items.filter(i => i.status === 'pending' || i.status === 'in_progress');
    const awaiting = items.filter(i => i.status === 'awaiting_approval');

    const ov = loadOverrides();
    const localArtifacts = ov.delegateArtifacts || {};
    // Filter orphaned research stubs
    const artifactEntries = Object.entries(localArtifacts).filter(([, art]) => {
      if (art.type === 'research') {
        const noCard = !art.cardName;
        const noInquiry = !art.inquiry && !art.question && !art.taskText;
        const noContent = !art.content || !art.content.trim();
        if (noCard && noInquiry && noContent) return false;
      }
      return true;
    });

    // Recent items (completed/discarded in last 48h)
    const now = Date.now();
    const h48 = 48 * 60 * 60 * 1000;
    const recent = items.filter(i => (i.status === 'done' || i.status === 'discarded' || i.status === 'approved') && i.createdAt && (now - new Date(i.createdAt).getTime()) < h48).slice(0, 5);

    // Total empty state
    if (!inProgress.length && !artifactEntries.length && !awaiting.length && !recent.length) {
      content.innerHTML = `<div class="mobile-cap-empty-state">
        <div class="mobile-cap-empty-sparkle">\u2726</div>
        ${e(AI_NAME)} is standing by.<br>Delegate tasks from any card.
      </div>`;
      return;
    }

    let html = '';

    // Pending count badge
    const reviewCount = artifactEntries.length + awaiting.length;

    // Batch review button
    if (reviewCount >= 3) {
      html += `<button class="mobile-cap-batch-btn" id="mobile-cap-batch-review">Review all (${reviewCount})</button>`;
    }

    // Awaiting Review (show first — more actionable)
    html += `<div class="mobile-section"><div class="mobile-section-header" style="color:#bb9af7">Awaiting your review${reviewCount > 0 ? ` <span class="section-count" style="background:#bb9af7;color:#1a1b26">${reviewCount}</span>` : ''}</div>`;
    if (artifactEntries.length) {
      for (const [key, art] of artifactEntries) {
        const typeLabel = art.type === 'email_draft' ? '\ud83d\udce7 Email draft' : art.type === 'research' ? '\ud83d\udd0d Research' : art.type === 'document' ? '\ud83d\udcc4 Document' : '\u25c8 Result';
        // Better subject-line labels per type
        let itemLabel;
        if (art.type === 'email_draft') {
          itemLabel = art.subject ? 'Email: ' + art.subject : (art.cardName || key);
        } else if (art.type === 'research') {
          const rq = art.inquiry || art.question || art.taskText;
          itemLabel = rq ? 'Research: ' + rq : (art.cardName || key);
        } else if (art.type === 'document') {
          itemLabel = art.title || art.taskText || art.cardName || key;
        } else {
          itemLabel = art.taskText || art.instruction || art.cardName || key;
        }
        const summary = (art.taskText || art.instruction || '').slice(0, 60);
        html += `<div class="mobile-card-item mobile-cap-review-item" data-artifact-key="${e(key)}">
          <span class="mobile-card-item-name">${typeLabel} \u2014 ${e(itemLabel)}</span>
          <div class="mobile-card-item-meta">
            <span>${e(summary)}</span>
          </div>
          <div class="mobile-cap-actions">
            <button class="mobile-cap-approve-btn" data-key="${e(key)}" data-delegate-id="${e(art.delegateId || '')}">✓ Approve</button>
            <button class="mobile-cap-reject-btn" data-key="${e(key)}">✏️ Changes</button>
            <button class="mobile-cap-discard-btn" data-key="${e(key)}" data-delegate-id="${e(art.delegateId || '')}">🗑</button>
          </div>
        </div>`;
      }
    } else if (awaiting.length) {
      for (const i of awaiting) {
        html += `<div class="mobile-card-item">
          <span class="mobile-card-item-name">${e(i.taskText || i.text || 'Result')}</span>
          <div class="mobile-card-item-meta"><span>${e(i.cardName || '')}</span></div>
        </div>`;
      }
    } else {
      html += '<div class="mobile-today-empty" style="padding:16px 0;font-size:0.82rem">Nothing awaiting review</div>';
    }
    html += '</div>';

    // In Progress — with pulse animation
    html += '<div class="mobile-section"><div class="mobile-section-header" style="color:#7aa2f7">In progress</div>';
    if (inProgress.length) {
      for (const i of inProgress) {
        html += `<div class="mobile-card-item mobile-cap-in-progress">
          <span class="mobile-card-item-name">${e(i.taskText || i.text || 'Task')}</span>
          <div class="mobile-card-item-meta"><span>${e(i.cardName || '')}</span></div>
        </div>`;
      }
    } else {
      html += '<div class="mobile-today-empty" style="padding:16px 0;font-size:0.82rem">No tasks in progress</div>';
    }
    html += '</div>';

    // Recent (last 5 completed/discarded, 48h)
    if (recent.length) {
      html += '<div class="mobile-section"><div class="mobile-section-header" style="color:var(--text-dim)">Recent</div>';
      for (const i of recent) {
        const icon = (i.status === 'done' || i.status === 'approved') ? '\u2713' : '\u2717';
        html += `<div class="mobile-card-item" style="opacity:0.6">
          <span class="mobile-card-item-name">${icon} ${e(i.taskText || i.instruction || i.text || 'Task')}</span>
          <div class="mobile-card-item-meta"><span>${e(i.cardName || '')}</span></div>
        </div>`;
      }
      html += '</div>';
    }

    content.innerHTML = html;

    // Wire approve buttons
    content.querySelectorAll('.mobile-cap-approve-btn').forEach(btn => {
      btn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const key = btn.dataset.key;
        const delegateId = btn.dataset.delegateId;
        mobileCapApprove(key, delegateId);
      });
    });

    // Wire reject (request changes) buttons
    content.querySelectorAll('.mobile-cap-reject-btn').forEach(btn => {
      btn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const key = btn.dataset.key;
        openMobileRevisionSheet(key);
      });
    });

    // Wire discard buttons
    content.querySelectorAll('.mobile-cap-discard-btn').forEach(btn => {
      btn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const key = btn.dataset.key;
        const delegateId = btn.dataset.delegateId;
        mobileCapDiscard(key, delegateId);
      });
    });

    // Wire artifact item taps → open artifact review sheet
    content.querySelectorAll('.mobile-cap-review-item').forEach(el => {
      el.addEventListener('click', (ev) => {
        if (ev.target.closest('.mobile-cap-actions')) return; // don't open if clicking a button
        const key = el.dataset.artifactKey;
        const ov2 = loadOverrides();
        const art = ov2.delegateArtifacts?.[key];
        if (art) openMobileArtifactReview(key, art);
      });
    });

    // Wire batch review
    const batchBtn = content.querySelector('#mobile-cap-batch-review');
    if (batchBtn) {
      batchBtn.addEventListener('click', () => {
        _mobileBatchReviewQueue = artifactEntries.map(([key]) => key);
        _mobileBatchReviewIdx = 0;
        if (_mobileBatchReviewQueue.length) showMobileBatchReview();
      });
    }

  } catch (_e) {
    content.innerHTML = `<div class="mobile-today-empty">Could not load ${e(AI_NAME)} data.</div>`;
  }
}

function mobileCapApprove(key, delegateId) {
  const ov = loadOverrides();
  const artifact = ov.delegateArtifacts?.[key];
  if (artifact) {
    const isCardLevel = key.startsWith('card:');
    const cardId = isCardLevel ? key.slice(5) : (artifact.cardId || '');
    writeDelegateHistory(key, cardId, artifact.type || 'result', 'approved');
  }
  // Remove artifact from local
  if (ov.delegateArtifacts) delete ov.delegateArtifacts[key];
  saveOverrides(ov);
  // Call approve API
  if (delegateId) {
    const hdrs = getAuthHeader();
    hdrs['Content-Type'] = 'application/json';
    fetch(`/api/v1/delegate/approve/${delegateId}`, { method: 'POST', headers: hdrs, body: JSON.stringify({}) })
      .catch(err => console.warn('[delegate] approve failed:', err.message));
  }
  showDelegateToast('Approved \u2713');
  updateMobileCapBadge();
  renderMobileCapView();
  renderFromState();
}

function openMobileRevisionSheet(artifactKey) {
  _mobileRevisionArtifactKey = artifactKey;
  const input = document.getElementById('mobile-revision-input');
  input.value = '';
  document.getElementById('mobile-revision-backdrop').classList.add('open');
  setTimeout(() => input.focus(), 300);
}

function closeMobileRevisionSheet() {
  document.getElementById('mobile-revision-backdrop').classList.remove('open');
  _mobileRevisionArtifactKey = null;
}

async function submitMobileRevision() {
  const key = _mobileRevisionArtifactKey;
  if (!key) return;
  const feedbackText = document.getElementById('mobile-revision-input').value.trim();
  if (!feedbackText) return;
  const ov = loadOverrides();
  const artifact = ov.delegateArtifacts?.[key];
  if (!artifact) { closeMobileRevisionSheet(); return; }
  const isCardLevel = key.startsWith('card:');
  const cardId = isCardLevel ? key.slice(5) : (artifact.cardId || '');
  const originalTask = artifact.taskText || key;
  const feedbackNote = `[Revision request: ${feedbackText}]\nOriginal result:\n${(artifact.content || '').slice(0, 500)}`;
  // Mark as revising (optimistic) instead of deleting — cleared when new result arrives
  if (!ov.delegateArtifacts) ov.delegateArtifacts = {};
  ov.delegateArtifacts[key] = { ...artifact, revising: true, revisingNote: feedbackText };
  saveOverrides(ov);
  // Re-enqueue with feedback
  try {
    await fetch('/api/v1/delegate', {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ cardId, cardName: artifact.cardName || '', taskId: key, taskText: `${originalTask} @delegate`, cardScope: '', cardNotes: feedbackNote }),
    });
  } catch (err) { console.warn('[delegate] revision re-queue failed:', err); }
  closeMobileRevisionSheet();
  showDelegateToast(`Sent to ${AI_NAME} for revision \u21bb`);
  updateMobileCapBadge();
  renderMobileCapView();
  renderFromState();
}

function showMobileBatchReview() {
  if (_mobileBatchReviewIdx >= _mobileBatchReviewQueue.length) {
    showDelegateToast('All reviewed \u2713');
    renderMobileCapView();
    return;
  }
  const key = _mobileBatchReviewQueue[_mobileBatchReviewIdx];
  const ov = loadOverrides();
  const art = ov.delegateArtifacts?.[key];
  if (!art) { _mobileBatchReviewIdx++; showMobileBatchReview(); return; }
  // Open a card-detail-like sheet for this artifact
  const typeLabel = art.type === 'email_draft' ? '\ud83d\udce7 Email draft' : art.type === 'research' ? '\ud83d\udd0d Research' : art.type === 'document' ? '\ud83d\udcc4 Document' : '\u25c8 Result';
  const body = document.getElementById('mobile-card-detail-body');
  const nameEl = document.getElementById('mobile-card-name');
  nameEl.contentEditable = 'false';
  nameEl.classList.remove('mobile-card-name-editable');
  nameEl.textContent = `${typeLabel} \u2014 ${art.cardName || key}`;
  document.getElementById('mobile-card-status').textContent = `${_mobileBatchReviewIdx + 1}/${_mobileBatchReviewQueue.length}`;
  document.getElementById('mobile-card-status').style.background = 'rgba(187,154,247,0.2)';
  document.getElementById('mobile-card-status').style.color = '#bb9af7';
  document.getElementById('mobile-card-context').textContent = '';
  body.innerHTML = `<div class="mobile-detail-section"><div class="mobile-detail-section-label">Result</div><pre style="font-size:0.85rem;color:var(--text);white-space:pre-wrap;line-height:1.5;margin:0">${e(art.content || '')}</pre></div>
    <div class="mobile-cap-actions" style="padding:12px 0">
      <button class="mobile-cap-approve-btn" id="mobile-batch-approve">✓ Approve</button>
      <button class="mobile-cap-reject-btn" id="mobile-batch-reject">↻ Changes</button>
    </div>`;
  // Hide delegate button in footer during batch review
  document.querySelector('.mobile-card-detail-footer').style.display = 'none';
  document.getElementById('mobile-card-backdrop').classList.add('open');
  document.getElementById('mobile-batch-approve').addEventListener('click', () => {
    mobileCapApprove(key, art.delegateId || '');
    _mobileBatchReviewIdx++;
    closeMobileCardDetail();
    document.querySelector('.mobile-card-detail-footer').style.display = '';
    setTimeout(() => showMobileBatchReview(), 300);
  });
  document.getElementById('mobile-batch-reject').addEventListener('click', () => {
    closeMobileCardDetail();
    document.querySelector('.mobile-card-detail-footer').style.display = '';
    openMobileRevisionSheet(key);
    _mobileBatchReviewIdx++;
  });
}

function updateMobileCapBadge() {
  const label = document.getElementById('mobile-cap-tab-label');
  const badge = document.getElementById('mobile-cap-badge');
  if (!label) return;
  const ov = loadOverrides();
  const count = Object.keys(ov.delegateArtifacts || {}).length;
  label.textContent = AI_NAME;
  if (badge) {
    if (count > 0) {
      badge.textContent = count;
      badge.style.display = '';
    } else {
      badge.style.display = 'none';
    }
  }
}

// ── Phase 2: Board View (swipe columns) ──
function renderMobileBoardView() {
  if (!boardData) return;
  const data = mergeOverrides(boardData);
  const ov = loadOverrides();
  _mobileBoardColumns = [];
  for (const col of data.columns) {
    if (ov.archivedColumns && ov.archivedColumns.includes(col.id)) continue;
    const colName = (ov.columnRenames && ov.columnRenames[col.id]) || col.name;
    _mobileBoardColumns.push({ id: col.id, name: colName, projects: col.projects || [] });
  }
  if (!_mobileBoardColumns.length) return;
  if (_mobileBoardColIdx >= _mobileBoardColumns.length) _mobileBoardColIdx = 0;

  const content = document.getElementById('mobile-board-content');
  let sliderHtml = '<div class="mobile-board-slider">';
  for (const col of _mobileBoardColumns) {
    sliderHtml += '<div class="mobile-board-column">';
    const cards = col.projects.filter(c => !c.archived && !(ov.archivedCards && ov.archivedCards[c.id]));
    if (!cards.length) {
      sliderHtml += '<div class="mobile-today-empty" style="padding:32px 0;font-size:0.82rem">No cards in this column</div>';
    } else {
      for (const card of cards) {
        const status = normalizeStatus(card.status || 'Active');
        const sc = STATUS_COLORS[status] || STATUS_COLORS['Active'];
        const dueStr = card.dueDate ? formatShortDate(card.dueDate) : '';
        const urgency = getDueDateUrgency(card.dueDate);
        const dueClass = urgency === 'overdue' ? ' overdue' : urgency === 'soon' ? ' soon' : '';
        sliderHtml += `<div class="mobile-card-item" data-card-id="${e(card.id)}" data-col-name="${e(col.name)}">
          <span class="mobile-card-item-name">${e(card.name)}</span>
          <div class="mobile-card-item-meta">
            <span class="mobile-card-status-chip" style="background:${sc.bg};color:${sc.color};font-size:0.68rem;padding:2px 6px">${e(status)}</span>
            ${dueStr ? `<span class="mobile-card-item-due${dueClass}">${e(dueStr)}</span>` : ''}
          </div>
        </div>`;
      }
    }
    sliderHtml += '</div>';
  }
  sliderHtml += '</div>';
  content.innerHTML = sliderHtml;
  navigateMobileBoard(_mobileBoardColIdx);

  // Wire card taps
  content.querySelectorAll('.mobile-card-item[data-card-id]').forEach(el => {
    el.addEventListener('click', () => {
      const cardId = el.dataset.cardId;
      const colName = el.dataset.colName;
      const d2 = mergeOverrides(boardData);
      let found = null;
      function find(cards) { for (const c of cards) { if (c.id === cardId) { found = c; return; } if (c.children?.length) find(c.children); } }
      for (const col of d2.columns) find(col.projects || []);
      if (found) openMobileCardDetail(found, colName);
    });
  });
}

function navigateMobileBoard(idx) {
  if (idx < 0 || idx >= _mobileBoardColumns.length) return;
  _mobileBoardColIdx = idx;
  const col = _mobileBoardColumns[idx];
  document.getElementById('mobile-board-col-name').textContent = col.name;
  // Render dots (carousel-style)
  const indicator = document.getElementById('mobile-board-indicator');
  indicator.innerHTML = _mobileBoardColumns.map((_c, i) =>
    `<span class="mobile-board-dot${i === idx ? ' active' : ''}"></span>`
  ).join('');
  document.getElementById('mobile-board-prev').disabled = idx === 0;
  document.getElementById('mobile-board-next').disabled = idx === _mobileBoardColumns.length - 1;
  const slider = document.querySelector('.mobile-board-slider');
  if (slider) slider.style.transform = `translateX(-${idx * 100}%)`;
}

// ── Phase 2: Voice Input ──
function initMobileVoiceInput() {
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SpeechRecognition) return; // API not supported — mic stays hidden
  const micBtn = document.getElementById('mobile-capture-mic');
  if (!micBtn) return;
  micBtn.style.display = 'flex';

  micBtn.addEventListener('click', () => {
    if (_mobileSpeechRecognition) {
      _mobileSpeechRecognition.stop();
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    _mobileSpeechRecognition = recognition;
    micBtn.classList.add('listening');

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      const input = document.getElementById('mobile-capture-input');
      input.value = transcript;
      // Trigger the parse flow
      onMobileCaptureInput();
      updateMobileCaptureDelegate();
    };

    recognition.onerror = () => {
      micBtn.classList.remove('listening');
      _mobileSpeechRecognition = null;
    };

    recognition.onend = () => {
      micBtn.classList.remove('listening');
      _mobileSpeechRecognition = null;
    };

    recognition.start();
  });
}

// ── Phase 2: @delegate from capture ──
function updateMobileCaptureDelegate() {
  const input = document.getElementById('mobile-capture-input');
  const submitBtn = document.getElementById('mobile-capture-submit');
  const text = input.value.trim();
  if (/^@delegate\b/i.test(text)) {
    submitBtn.textContent = 'Hand off to ' + AI_NAME + ' \u2192';
    submitBtn.classList.add('delegate-mode');
  } else {
    submitBtn.textContent = 'Add';
    submitBtn.classList.remove('delegate-mode');
  }
}

// ── Phase 2: Cap badge polling ──
function startMobileCapBadgePoll() {
  // Initial update
  updateMobileCapBadge();
  // Poll every 60s
  _mobileCapBadgePollTimer = setInterval(async () => {
    if (!isMobileViewport()) return;
    try {
      await refreshDelegateQueue();
      updateMobileCapBadge();
    } catch { /* silent */ }
  }, 60000);
}

// ── Phase 2: Discard with undo toast ──
function mobileCapDiscard(key, delegateId) {
  const ov = loadOverrides();
  const artifact = ov.delegateArtifacts?.[key];
  const snap = artifact ? JSON.parse(JSON.stringify(artifact)) : null;
  if (ov.delegateArtifacts) delete ov.delegateArtifacts[key];
  saveOverrides(ov);
  renderFromState();
  showDiscardUndoToast(key, delegateId, snap);
  renderMobileCapView();
}

// ── Phase 2: Artifact Review Sheet ──
let _mobileCurrentArtifactKey = null;

function openMobileArtifactReview(key, artifact) {
  _mobileCurrentArtifactKey = key;
  const backdrop = document.getElementById('mobile-artifact-backdrop');

  // Title — subject-line style
  let sheetTitle;
  if (artifact.type === 'email_draft') {
    sheetTitle = artifact.subject ? 'Email: ' + artifact.subject : 'Email Draft';
  } else if (artifact.type === 'research') {
    const rq = artifact.inquiry || artifact.question || artifact.taskText;
    sheetTitle = rq ? 'Research: ' + rq : 'Research';
  } else if (artifact.type === 'document') {
    sheetTitle = artifact.title || artifact.taskText || 'Document';
  } else {
    sheetTitle = artifact.type === 'analysis' ? 'Analysis' : (artifact.type || 'Result');
  }
  document.getElementById('mobile-artifact-title').textContent = sheetTitle;

  // Meta
  const meta = document.getElementById('mobile-artifact-meta');
  let metaHtml = '';
  if (artifact.cardName) metaHtml += `<div class="mobile-artifact-meta-row"><strong>Card:</strong> ${e(artifact.cardName)}</div>`;
  if (artifact.taskText || artifact.instruction) metaHtml += `<div class="mobile-artifact-meta-row"><strong>Asked:</strong> ${e(artifact.taskText || artifact.instruction)}</div>`;
  meta.innerHTML = metaHtml;

  // Content
  const contentEl = document.getElementById('mobile-artifact-content');
  if (artifact.type === 'email_draft') {
    contentEl.innerHTML = `<div class="mobile-artifact-email">
      ${artifact.to || artifact.recipient ? `<div class="mobile-artifact-email-field"><strong>To:</strong> ${e(artifact.to || artifact.recipient)}</div>` : ''}
      ${artifact.subject ? `<div class="mobile-artifact-email-field"><strong>Subject:</strong> ${e(artifact.subject)}</div>` : ''}
      <div class="mobile-artifact-email-body">${e(artifact.content || '')}</div>
    </div>`;
  } else if (artifact.type === 'research') {
    let rhtml = '';
    const inquiry = artifact.inquiry || artifact.question || artifact.taskText;
    if (inquiry) rhtml += `<div class="mobile-artifact-section"><strong>Inquiry</strong><p>${e(inquiry)}</p></div>`;
    const context = artifact.context || artifact.background;
    if (context) rhtml += `<div class="mobile-artifact-section"><strong>Context</strong><p>${e(context)}</p></div>`;
    if (artifact.content) rhtml += `<div class="mobile-artifact-section"><strong>Findings</strong><p>${e(artifact.content)}</p></div>`;
    const impact = artifact.impact || artifact.implications;
    if (impact) rhtml += `<div class="mobile-artifact-section"><strong>Impact</strong><p>${e(impact)}</p></div>`;
    const decision = artifact.decision || artifact.recommendation;
    if (decision) rhtml += `<div class="mobile-artifact-section"><strong>Decision / Recommendation</strong><p>${e(decision)}</p></div>`;
    contentEl.innerHTML = rhtml || `<div class="mobile-artifact-body-text">${e(artifact.content || '')}</div>`;
  } else {
    contentEl.innerHTML = `<div class="mobile-artifact-body-text">${e(artifact.content || '')}</div>`;
  }

  // Actions
  const actions = document.getElementById('mobile-artifact-actions');
  const isEmail = artifact.type === 'email_draft';
  actions.innerHTML = `
    <button class="mobile-artifact-approve-btn" id="mobile-art-approve">${isEmail ? '\u2705 Approve & Send' : '\u2705 Approve'}</button>
    <button class="mobile-artifact-revise-btn" id="mobile-art-revise">\u270f\ufe0f Request Changes</button>
    <button class="mobile-artifact-discard-btn" id="mobile-art-discard">\ud83d\uddd1 Discard</button>`;

  // Wire approve
  document.getElementById('mobile-art-approve').addEventListener('click', () => {
    if (isEmail) {
      showSendConfirmModal(artifact, () => {
        mobileCapApprove(key, artifact.delegateId || '');
        closeMobileArtifactReview();
      });
      return;
    }
    mobileCapApprove(key, artifact.delegateId || '');
    closeMobileArtifactReview();
  });

  // Wire request changes
  document.getElementById('mobile-art-revise').addEventListener('click', () => {
    closeMobileArtifactReview();
    openMobileRevisionSheet(key);
  });

  // Wire discard
  document.getElementById('mobile-art-discard').addEventListener('click', () => {
    closeMobileArtifactReview();
    mobileCapDiscard(key, artifact.delegateId || '');
  });

  backdrop.classList.add('open');
}

function closeMobileArtifactReview() {
  document.getElementById('mobile-artifact-backdrop').classList.remove('open');
  _mobileCurrentArtifactKey = null;
}

// ── Phase 2: Saved toast ──
let _mobileSavedToastTimer = null;
function showMobileSavedToast() {
  if (!isMobileViewport()) return;
  const toast = document.getElementById('mobile-saved-toast');
  if (!toast) return;
  toast.classList.add('visible');
  clearTimeout(_mobileSavedToastTimer);
  _mobileSavedToastTimer = setTimeout(() => toast.classList.remove('visible'), 1500);
}

// ── Sheet swipe-to-dismiss ──
function setupSheetSwipeDismiss(sheetEl, closeFn) {
  let startY = 0;
  let currentY = 0;
  let dragging = false;

  sheetEl.addEventListener('touchstart', (ev) => {
    const handle = sheetEl.querySelector('.mobile-sheet-handle');
    if (!handle) return;
    const touch = ev.touches[0];
    const rect = handle.getBoundingClientRect();
    // Only start drag from handle area (top 40px)
    if (touch.clientY < rect.top - 10 || touch.clientY > rect.bottom + 30) return;
    startY = touch.clientY;
    dragging = true;
    sheetEl.style.transition = 'none';
  }, { passive: true });

  sheetEl.addEventListener('touchmove', (ev) => {
    if (!dragging) return;
    currentY = ev.touches[0].clientY;
    const dy = Math.max(0, currentY - startY);
    sheetEl.style.transform = `translateY(${dy}px)`;
  }, { passive: true });

  sheetEl.addEventListener('touchend', () => {
    if (!dragging) return;
    dragging = false;
    sheetEl.style.transition = '';
    const dy = currentY - startY;
    if (dy > 80) {
      closeFn();
    }
    sheetEl.style.transform = '';
  });
}

// ── Responsive layout switching (≤520px → mobile, >520px → desktop) ──
let _kapboardMode = window.innerWidth <= 520 ? 'mobile' : 'desktop';
let _modeDebounce;
window.addEventListener('resize', () => {
  clearTimeout(_modeDebounce);
  _modeDebounce = setTimeout(() => {
    const newMode = window.innerWidth <= 520 ? 'mobile' : 'desktop';
    if (newMode === _kapboardMode) return;
    _kapboardMode = newMode;
    if (newMode === 'mobile') {
      document.getElementById('mobile-shell').style.display = 'flex';
      initMobile();
      if (boardData) { renderMobileTodayView(); renderMobileDate(); }
    } else {
      document.getElementById('mobile-shell').style.display = '';
      if (boardData) renderFromState();
    }
  }, 50);
});

// ── Offline / Online banner ──
window.addEventListener('offline', () => {
  const banner = document.getElementById('offline-banner');
  if (banner) banner.classList.remove('hidden');
});
window.addEventListener('online', () => {
  const banner = document.getElementById('offline-banner');
  if (banner) banner.classList.add('hidden');
  // Re-sync when coming back online
  cloudSave(loadOverrides());
});
if (!navigator.onLine) {
  const banner = document.getElementById('offline-banner');
  if (banner) banner.classList.remove('hidden');
}
